import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Edit3, AlertCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { 
  FORMULARY_CATEGORIES,
  FORMULARY_FORMS,
  FORMULARY_ROUTES,
  adminApi,
  type AdminFormularyItem as FormularyItem
} from '@/services/adminService';
import { selectAdminPharmacies } from '@/store/slices/adminSlice';

interface PharmacyEditData {
  selected: boolean;
  price: number;
}

interface MedicationEditData {
  id: string;
  href: string;
  index: number;
  name: string;
  amount: number;
  unit: string;
}

interface FormData {
  category: string;
  form: string;
  route: string;
  quantity: number;
  unit: string;
  medications: MedicationEditData[]; // Array of medications
  pharmacies: Record<string, PharmacyEditData>; // pharmacyId -> edit data
}

interface EditFormularyModalProps {
  isOpen: boolean;
  onClose: () => void;
  formulary: FormularyItem | null;
  onUpdate: () => void;
}

const EditFormularyModal: React.FC<EditFormularyModalProps> = ({
  isOpen,
  onClose,
  formulary,
  onUpdate
}) => {
  // Get all available pharmacies from Redux cache
  const allPharmacies = useSelector(selectAdminPharmacies);
  // Initialize form data when formulary changes
  const getInitialFormData = (): FormData => {
    if (!formulary) {
      return {
        category: '',
        form: '',
        route: '',
        quantity: 0,
        unit: '',
        medications: [],
        pharmacies: {}
      };
    }

    // Initialize pharmacy data directly from the formulary's pharmacy array
    const pharmacyData: Record<string, PharmacyEditData> = {};
    
    // Add existing pharmacies from formulary
    formulary.pharmacies.forEach(pharmacy => {
      pharmacyData[pharmacy.id] = {
        selected: pharmacy.available, // Use the 'available' field directly
        price: pharmacy.price
      };
    });
    
    // Add missing pharmacies from the complete list (not selected, default price)
    allPharmacies.forEach(pharmacy => {
      if (!pharmacyData[pharmacy.id]) {
        pharmacyData[pharmacy.id] = {
          selected: false,
          price: 100.00 // Default price for new pharmacies
        };
      }
    });

    return {
      category: formulary.category,
      form: formulary.form,
      route: formulary.route,
      quantity: formulary.formulary_size.amount,
      unit: formulary.formulary_size.unit,
      medications: formulary.medications || [],
      pharmacies: pharmacyData
    };
  };

  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form data when formulary changes
  React.useEffect(() => {
    if (isOpen && formulary) {
      setFormData(getInitialFormData());
      setError(null);
    }
  }, [isOpen, formulary]);

  const handleInputChange = (field: keyof Omit<FormData, 'pharmacies'>, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handlePharmacyChange = (pharmacyId: string, field: keyof PharmacyEditData, value: boolean | number) => {
    setFormData(prev => ({
      ...prev,
      pharmacies: {
        ...prev.pharmacies,
        [pharmacyId]: {
          ...prev.pharmacies[pharmacyId],
          [field]: value
        }
      }
    }));
    if (error) setError(null);
  };

  const handleMedicationChange = (index: number, field: keyof Omit<MedicationEditData, 'id' | 'href' | 'index'>, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.map((med, i) => 
        i === index 
          ? { ...med, [field]: value }
          : med
      )
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formulary) return;

    if (!formData.category || !formData.form || !formData.route) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate that all selected pharmacies have prices
    const selectedPharmacies = Object.entries(formData.pharmacies).filter(([_, data]) => data.selected);
    const missingPrices = selectedPharmacies.filter(([_, data]) => !data.price || data.price <= 0);
    
    if (missingPrices.length > 0) {
      setError('All selected pharmacies must have a price greater than $0');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Update formulary basic data
      await adminApi.updateFormulary({
        id: formulary.id,
        name: formulary.medications?.[0]?.name || 'Unknown',
        category: formData.category,
        form: formData.form,
        route: formData.route,
        quantity: formData.quantity,
        unit: formData.unit
      });

      // Update pharmacy-formulary relationships and pricing
      const selectedPharmacyIds = Object.keys(formData.pharmacies).filter(id => formData.pharmacies[id].selected);
      const currentPharmacyIds = formulary.pharmacies.map(p => p.id);

      // Add new pharmacy relationships (pharmacies that are now selected but weren't in the original list)
      const pharmaciesToAdd = selectedPharmacyIds.filter(id => !currentPharmacyIds.includes(id));
      for (const pharmacyId of pharmaciesToAdd) {
        try {
          await adminApi.addPharmacyToFormulary(
            pharmacyId,
            formulary.id,
            formData.pharmacies[pharmacyId].price
          );
        } catch (err) {
          console.warn(`Failed to add pharmacy ${pharmacyId} to formulary:`, err);
        }
      }

      // Remove pharmacy relationships (pharmacies that were in original list but are now unselected)
      const pharmaciesToRemove = currentPharmacyIds.filter(id => !selectedPharmacyIds.includes(id));
      for (const pharmacyId of pharmaciesToRemove) {
        try {
          await adminApi.removePharmacyFromFormulary(pharmacyId, formulary.id);
        } catch (err) {
          console.warn(`Failed to remove pharmacy ${pharmacyId} from formulary:`, err);
        }
      }

      // Update prices for existing selected pharmacies (ones that were in original list and are still selected)
      const pharmaciesToUpdate = selectedPharmacyIds.filter(id => currentPharmacyIds.includes(id));
      for (const pharmacyId of pharmaciesToUpdate) {
        try {
          await adminApi.updatePricing({
            pharmacyId,
            formularyId: formulary.id,
            price: formData.pharmacies[pharmacyId].price
          });
        } catch (err) {
          console.warn(`Failed to update pricing for pharmacy ${pharmacyId}:`, err);
        }
      }

      // Success - close modal and refresh data
      onUpdate();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update formulary');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(getInitialFormData());
    setError(null);
    onClose();
  };

  if (!formulary) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto border-l-4 border-l-emerald-500">
        <DialogHeader>
          <DialogTitle className="font-syne font-medium text-vitl-navy">
            Edit Formulary
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Category *
              </Label>
              <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                <SelectTrigger className="font-lato">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {FORMULARY_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Form *
              </Label>
              <Select value={formData.form} onValueChange={(value) => handleInputChange('form', value)}>
                <SelectTrigger className="font-lato">
                  <SelectValue placeholder="Select form" />
                </SelectTrigger>
                <SelectContent>
                  {FORMULARY_FORMS.map((form) => (
                    <SelectItem key={form.value} value={form.value}>{form.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
              Route *
            </Label>
            <Select value={formData.route} onValueChange={(value) => handleInputChange('route', value)}>
              <SelectTrigger className="font-lato">
                <SelectValue placeholder="Select route" />
              </SelectTrigger>
              <SelectContent>
                {FORMULARY_ROUTES.map((route) => (
                  <SelectItem key={route.value} value={route.value}>{route.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Quantity
              </Label>
              <Input
                type="number"
                value={formData.quantity}
                onChange={(e) => handleInputChange('quantity', parseFloat(e.target.value) || 0)}
                placeholder="0"
                className="font-lato"
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                Unit
              </Label>
              <Input
                value={formData.unit}
                onChange={(e) => handleInputChange('unit', e.target.value)}
                placeholder="mg, ml, etc."
                className="font-lato"
              />
            </div>
          </div>

          {/* Medications Section */}
          {formData.medications.length > 0 && (
            <div className="space-y-4 border-t pt-4">
              <div>
                <h4 className="text-sm font-lato font-medium text-vitl-dark/70 mb-3">
                  Medications
                </h4>
                <div className="space-y-3">
                  {formData.medications.map((medication, index) => (
                    <div key={medication.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                            Medication Name
                          </Label>
                          <Input
                            value={medication.name}
                            onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                            className="font-lato"
                            placeholder="Medication name"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                              Amount
                            </Label>
                            <Input
                              type="number"
                              value={medication.amount}
                              onChange={(e) => handleMedicationChange(index, 'amount', parseFloat(e.target.value) || 0)}
                              className="font-lato"
                              min="0"
                              step="0.1"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-lato font-medium text-vitl-dark/70 mb-2 block">
                              Unit
                            </Label>
                            <Input
                              value={medication.unit}
                              onChange={(e) => handleMedicationChange(index, 'unit', e.target.value)}
                              className="font-lato"
                              placeholder="mg, ml, etc."
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Pharmacy Management Section */}
          <div className="space-y-4 border-t pt-4">
            <div>
              <h4 className="text-sm font-lato font-medium text-vitl-dark/70 mb-3">
                Current Pharmacies
              </h4>
              <div className="space-y-3">
                {formulary?.pharmacies?.length === 0 ? (
                  <p className="text-sm text-vitl-dark/50 font-lato text-center py-4">
                    No pharmacies currently carry this formulary
                  </p>
                ) : (
                  formulary?.pharmacies?.map((pharmacy) => {
                    const isAvailable = formData.pharmacies[pharmacy.id]?.selected || false;
                    const currentPrice = formData.pharmacies[pharmacy.id]?.price || 100.00;
                    
                    return (
                      <div key={pharmacy.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center space-x-3">
                          <Switch
                            id={`current-pharmacy-${pharmacy.id}`}
                            checked={isAvailable}
                            onCheckedChange={(checked) => handlePharmacyChange(pharmacy.id, 'selected', checked)}
                          />
                          <div className="flex-1">
                            <label
                              htmlFor={`current-pharmacy-${pharmacy.id}`}
                              className="text-sm font-lato font-medium text-vitl-navy cursor-pointer"
                            >
                              {pharmacy.name}
                            </label>
                            <p className="text-xs text-vitl-dark/50 font-lato">
                              {isAvailable ? 'Available' : 'Not Available'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {isAvailable ? (
                            <>
                              <span className="text-sm font-lato text-vitl-dark/70">$</span>
                              <Input
                                type="number"
                                value={currentPrice}
                                onChange={(e) => {
                                  const price = parseFloat(e.target.value) || 0;
                                  handlePharmacyChange(pharmacy.id, 'price', price);
                                }}
                                className="w-24 h-8 text-sm font-lato"
                                min="0"
                                step="0.01"
                              />
                            </>
                          ) : (
                            <span className="text-sm font-lato text-vitl-dark/40">
                              ${currentPrice.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Available to Add Section */}
            <div>
              <h4 className="text-sm font-lato font-medium text-vitl-dark/70 mb-3">
                Add New Pharmacies
              </h4>
              <div className="space-y-3">
                {allPharmacies
                  .filter(pharmacy => !formulary?.pharmacies?.some(fp => fp.id === pharmacy.id))
                  .map((pharmacy) => {
                    const isSelected = formData.pharmacies[pharmacy.id]?.selected || false;
                    const currentPrice = formData.pharmacies[pharmacy.id]?.price || 100.00;
                    
                    return (
                      <div key={pharmacy.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Switch
                            id={`new-pharmacy-${pharmacy.id}`}
                            checked={isSelected}
                            onCheckedChange={(checked) => handlePharmacyChange(pharmacy.id, 'selected', checked)}
                          />
                          <div className="flex-1">
                            <label
                              htmlFor={`new-pharmacy-${pharmacy.id}`}
                              className="text-sm font-lato font-medium text-vitl-navy cursor-pointer"
                            >
                              {pharmacy.name}
                            </label>
                            <p className="text-xs text-vitl-dark/50 font-lato">
                              {isSelected ? 'Will be added' : 'Not selected'}
                            </p>
                          </div>
                        </div>
                        
                        {isSelected && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-lato text-vitl-dark/70">$</span>
                            <Input
                              type="number"
                              value={currentPrice}
                              onChange={(e) => {
                                const price = parseFloat(e.target.value) || 0;
                                handlePharmacyChange(pharmacy.id, 'price', price);
                              }}
                              className="w-24 h-8 text-sm font-lato"
                              min="0"
                              step="0.01"
                              placeholder="Required"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                <p className="text-red-700 font-lato text-sm">{error}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="font-lato"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-lato font-medium"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Edit3 className="w-4 h-4 mr-2" />
                  Update Formulary
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditFormularyModal;